import { cssVar } from '@mfw/common';
import type { SwitchProps } from '@mui/material/Switch';
import type { Theme } from '@mui/material/styles';

import { switchClasses } from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';

import type { OnSurfaceMarkFamily } from '../../onSurface';

import { ON_SURFACE_MARK_FAMILIES, onSurfaceMark } from '../../onSurface';

// ----------------------------------------------------------------------

export function switches(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: SwitchProps) => {
    const { color } = ownerState;

    return {
      [`& .${switchClasses.switchBase}`]: {
        [`&.${switchClasses.checked}`]: {
          [`& .${switchClasses.thumb}`]: {
            ...(color === 'default' &&
              !lightMode && {
                color: theme.palette.grey[800],
              }),
          },
          [`&+.${switchClasses.track}`]: {
            opacity: 1,
            ...(color === 'default' && {
              backgroundColor: cssVar.text.primary,
            }),
            // A switch that is ON says so with its TRACK: the whole body of the
            // control turns the family colour, so that colour is the state mark
            // and takes the step measured at 3:1 against the page and the cards
            // (MUI's `<color>.main` is 1.7:1 on a light tenant). The THUMB
            // stays the white this file pins below and rides on the track; the
            // track is not always dark enough to carry it — when no brand step
            // reads, the mark falls back to the body ink, which is WHITE for a
            // navy or near-black brand on a dark tile — so the thumb carries
            // its own edge (below). The hover wash MUI derives from `main` is
            // a fill and stays.
            ...(ON_SURFACE_MARK_FAMILIES.includes(
              color as OnSurfaceMarkFamily,
            ) && {
              backgroundColor: onSurfaceMark[color as OnSurfaceMarkFamily],
            }),
          },
          transform: 'translateX(13px)',
        },
        [`&.${switchClasses.disabled}`]: {
          [`& .${switchClasses.thumb}`]: {
            opacity: lightMode ? 1 : 0.48,
          },
          [`&+.${switchClasses.track}`]: {
            opacity: 0.48,
          },
        },
        left: 3,
        padding: 12,
      },
      [`& .${switchClasses.thumb}`]: {
        // An edge of its own, so the white thumb stays visible on whatever the
        // track turns: the measured mark can be white (see the checked track
        // above), and pale brand tracks (yellow 1.40:1, the default green
        // 1.90:1) were already close to white. The ring is a non-text edge:
        // 45% black composites to 3.35:1 on a white track (40% fell to 2.85).
        // It shows on a light track and melts into a dark one, where the white
        // thumb needs no help.
        boxShadow:
          '0 0 0 1px rgba(0, 0, 0, 0.45), 0 1px 2px 0 rgba(0, 0, 0, 0.24)',
        color: theme.palette.common.white,
        height: 14,
        width: 14,
      },
      [`& .${switchClasses.track}`]: {
        backgroundColor: alpha(theme.palette.grey[500], 0.48),
        borderRadius: 14,
        opacity: 1,
      },
      // Small
      [`&.${switchClasses.sizeSmall}`]: {
        [`& .${switchClasses.switchBase}`]: {
          [`&.${switchClasses.checked}`]: {
            transform: 'translateX(9px)',
          },
          padding: 7,
        },
        [`& .${switchClasses.thumb}`]: {
          height: 10,
          width: 10,
        },
        height: 24,
        padding: '4px 8px 4px 7px',
        width: 40,
      },
      height: 38,
      padding: '9px 13px 9px 12px',
      width: 58,
    };
  };

  return {
    MuiSwitch: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: SwitchProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
